import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Modal,
  Segment,
} from 'semantic-ui-react';
import {
  ContractLineAuditLogBucketDto,
  ContractLineAuditLogDto,
  contractService,
} from '../../services/contracts/ContractService';
import { LineAuditTimeline } from './LineAuditTimeline';
import {
  getContractTypeLabel,
  isAditivoContract,
  isRenegociacaoContract,
  sortContractsForVtmeGroupOrder,
} from '../../utils/contractGroupVtmeOrder';

export interface ContractLineAuditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'solo' | 'group';
  contractId?: string;
  groupId?: string;
}

type LoadState = {
  loading: boolean;
  error: string | null;
  soloLogs: ContractLineAuditLogDto[];
  groupBuckets: ContractLineAuditLogBucketDto[];
};

const emptyLoadState: LoadState = {
  loading: false,
  error: null,
  soloLogs: [],
  groupBuckets: [],
};

const ContractLineAuditHistoryModal: React.FC<ContractLineAuditHistoryModalProps> = ({
  isOpen,
  onClose,
  mode,
  contractId,
  groupId,
}) => {
  const [state, setState] = useState<LoadState>(emptyLoadState);

  useEffect(() => {
    if (!isOpen) {
      setState(emptyLoadState);
      return;
    }

    let cancelled = false;

    if (mode === 'solo') {
      if (!contractId) {
        setState({ ...emptyLoadState, error: 'Contrato não informado.' });
        return;
      }
      setState({ ...emptyLoadState, loading: true });
      contractService
        .getContractLineAuditLog(contractId)
        .then((logs) => {
          if (!cancelled) setState({ ...emptyLoadState, soloLogs: logs });
        })
        .catch((err: any) => {
          if (cancelled) return;
          const status = err?.response?.status;
          const message =
            status === 403
              ? 'Acesso negado ao histórico de linhas deste contrato.'
              : err?.response?.data?.message || 'Erro ao carregar histórico de linhas.';
          setState({ ...emptyLoadState, error: message });
        });
    } else {
      if (!groupId) {
        setState({ ...emptyLoadState, error: 'Grupo não informado.' });
        return;
      }

      setState({ ...emptyLoadState, loading: true });
      contractService
        .getGroupLineAuditLog(groupId)
        .then((response) => {
          if (!cancelled) setState({ ...emptyLoadState, groupBuckets: response.contracts ?? [] });
        })
        .catch((err: any) => {
          if (cancelled) return;
          const status = err?.response?.status;
          const message =
            status === 403
              ? 'Acesso negado ao histórico de linhas deste grupo.'
              : err?.response?.data?.message || 'Erro ao carregar histórico de linhas do grupo.';
          setState({ ...emptyLoadState, error: message });
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, contractId, groupId]);

  const columns = useMemo(() => {
    if (mode !== 'group') return [];

    const sortable = state.groupBuckets.map((bucket) => ({
      id: bucket.contractId,
      contractType: bucket.contractType,
      tipoContratacao: bucket.tipoContratacao,
      bucket,
    }));
    const sorted = sortContractsForVtmeGroupOrder(sortable);
    const aditivo =
      sorted.find((item) => isAditivoContract(item)) ??
      sorted[0] ??
      null;
    const renegociacao =
      sorted.find((item) => isRenegociacaoContract(item)) ??
      sorted.find((item) => item.id !== aditivo?.id) ??
      null;

    return [
      { key: 'aditivo' as const, title: 'Aditivo', item: aditivo },
      { key: 'renegociacao' as const, title: 'Renegociação', item: renegociacao },
    ];
  }, [mode, state.groupBuckets]);

  const totalCount =
    mode === 'solo'
      ? state.soloLogs.length
      : state.groupBuckets.reduce((sum, bucket) => sum + (bucket.logs?.length ?? 0), 0);

  return (
    <Modal open={isOpen} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="history" />
        {mode === 'group' ? 'Histórico de linhas do grupo' : 'Histórico de linhas do contrato'}
      </Modal.Header>

      <Modal.Content scrolling style={{ maxHeight: '70vh' }}>
        {state.loading && (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <Loader active inline="centered" />
            <div style={{ marginTop: '12px', color: '#666' }}>Carregando histórico...</div>
          </div>
        )}

        {!state.loading && state.error && <Message negative icon="warning circle" content={state.error} />}

        {!state.loading && !state.error && mode === 'solo' && (
          <LineAuditTimeline logs={state.soloLogs} />
        )}

        {!state.loading && !state.error && mode === 'group' && (
          <Grid columns={2} divided stackable>
            {columns.map(({ key, title, item }) => (
              <Grid.Column key={key}>
                <Segment style={{ minHeight: '420px' }}>
                  <Header size="medium">
                    <Icon name={key === 'aditivo' ? 'plus circle' : 'refresh'} />
                    <Header.Content>
                      {title}
                      {item && (
                        <Header.Subheader>
                          {getContractTypeLabel(item)}
                          {' · '}
                          ID {item.id.slice(0, 8)}…
                          {item.bucket.contractIsDeleted && (
                            <Label color="red" size="mini" style={{ marginLeft: 8 }}>
                              Contrato excluído
                            </Label>
                          )}
                        </Header.Subheader>
                      )}
                    </Header.Content>
                  </Header>

                  {!item ? (
                    <Message info>
                      <Icon name="info circle" />
                      Nenhum contrato de {title} neste grupo.
                    </Message>
                  ) : (
                    <LineAuditTimeline logs={item.bucket.logs ?? []} />
                  )}
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Label size="small" style={{ float: 'left', marginTop: '8px' }}>
          <Icon name="info circle" />
          Total: {totalCount} registro(s)
        </Label>
        <Button onClick={onClose} primary>
          <Icon name="check" />
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ContractLineAuditHistoryModal;
